import React, { useState, useMemo, useCallback, useRef } from "react";
import {
  Card,
  Select,
  Row,
  Col,
  Typography,
  Form,
  Divider,
  Button,
  Space,
  Spin,
  message,
  Modal,
  Input,
  Alert,
} from "antd";
import { SearchOutlined, CopyOutlined, CodeOutlined, EditOutlined } from "@ant-design/icons";
import { Content } from "antd/es/layout/layout";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import mercados from "./usda/mercados.json";
import atributosData from "./usda/atributos.json";
import paisRegiao from "./usda/paisRegiao.json";
import { request } from "../../functions/request";
import { buildMiniChartOptions, formatarNaMedida, generateEmbedHtml, medidaUsda, specUsdaParaGerador } from "./usda/miniGrafico";
import { useNavigate } from "react-router-dom";
import { CHAVE_SPEC_IMPORTADO } from "../Graficos/GeradorGraficos";

const { Title } = Typography;

// ---------- Constantes da query USDA ----------
const COMMODITY_CODES = [
  "0011000","0013000","0430000","0711100","0440000","2631000","0000000",
  "0813700","0813300","0813800","0813200","0813600","0813100","0813101",
  "0813500","0111000","0115000","0113000","0459100","0459900","0452000",
  "4242000","4233000","4235000","4243000","4244000","4234000","4239100",
  "4232000","4232001","4236000","2231000","2223000","2232000","2221000",
  "2226000","2222000","2222001","2224000","0114200","0422110","0451000",
  "0459200","0410000",
  // Laticínios (Dairy)
  "0223000","0224200","0224400","0230000","0240000",
];

const ATTRIBUTE_IDS = [
  1,4,5,6,7,20,22,23,25,28,29,30,31,32,33,34,43,47,48,49,53,54,56,57,
  58,62,63,64,65,71,74,75,81,82,84,86,88,89,90,94,99,104,107,113,114,
  117,118,121,122,125,126,128,130,131,132,133,135,139,140,141,142,147,
  149,150,151,152,154,155,158,161,167,169,172,173,174,175,176,178,181,
  182,183,184,192,194,195,
];

// Até o ano-safra que vem: o USDA publica a projeção do próximo ano a
// partir de maio. Antes estava fixo em 2026 e travaria a tela em 2027.
const ULTIMO_ANO = new Date().getFullYear() + 1;
const YEARS = Array.from({ length: ULTIMO_ANO - 1960 + 1 }, (_, i) => ULTIMO_ANO - i);

// Tipos de mercado cujo ano comercial no PSD é o ano civil (ver o rótulo
// montado na leitura da resposta).
const MERCADOS_POR_ANO = new Set(["Proteína", "Laticínios"]);

// Linha agregada do próprio USDA. Em "Mundo" é ela que vale; numa soma de
// países ela entraria em dobro (o total mundial saía o dobro do real).
const CODIGO_MUNDO = "R00";

/**
 * Unidades que não se somam entre países. Produtividade de uma região é
 * produção / área (as duas somadas); percentual e razão não têm como ser
 * reconstruídos aqui, então ficam de fora do agregado em vez de sair um
 * número errado (antes a produtividade "mundial" da soja saía 10 t/ha).
 */
const unidadeNaoSomavel = (u) => /mt\/ha|kg\/ha|percent|ratio/i.test(u || "");
const KG_POR_FARDO = 217.7243; // fardo de 480 lb, para a produtividade do algodão em kg/ha

function produtividadeAgregada(pontos, todos) {
  const { commodity, unit } = pontos[0];
  const kgHa = /kg\/ha/i.test(unit);
  if (!/mt\/ha|kg\/ha/i.test(unit)) return null;
  const soma = (attr, safra) =>
    todos
      .filter((d) => d.commodity === commodity && d.attribute === attr && d.safra === safra)
      .reduce((s, d) => s + d.valorFinal, 0);
  const safras = [...new Set(pontos.map((p) => p.safra))];
  const saida = [];
  for (const safra of safras) {
    const area = soma("Area Harvested", safra);
    const prod = soma("Production", safra);
    if (!area || !prod) continue;
    const base = pontos.find((p) => p.safra === safra);
    saida.push({ ...base, valorFinal: kgHa ? (prod * KG_POR_FARDO) / area : prod / area });
  }
  return saida;
}

// ---------- Lookup maps ----------
const mercadoMap = new Map();
for (const m of mercados) mercadoMap.set(m.NameCommodity.toLowerCase().trim(), m);

const atributoMap = new Map();
for (const a of atributosData) atributoMap.set(a.Attribute.toLowerCase().trim(), a.Atributo);

const paisBySigla = new Map();
for (const p of paisRegiao) if (!paisBySigla.has(p.Sigla)) paisBySigla.set(p.Sigla, p);

const paisByCountry = new Map();
for (const p of paisRegiao) {
  const k = p.Country.toLowerCase().trim();
  if (!paisByCountry.has(k)) paisByCountry.set(k, p);
}

const regioes = [...new Set(paisRegiao.map((p) => p.Regiao))].sort();
const tiposMercado = [...new Set(mercados.map((m) => m.TipoMercado))].sort();

// ---------- Processamento da resposta (equivale ao Power Query) ----------
export function processApiResponse(queryResult) {
  if (!queryResult?.length) return [];

  const idCols = [
    "cm_Code","commodity","attribute_Id","attribute",
    "cn_Code","country","unit Description",
  ];

  const lastVals = {};
  const filled = queryResult.map((row) => {
    const r = { ...row };
    for (const col of idCols) {
      if (r[col] != null && r[col] !== "") {
        lastVals[col] = r[col];
      } else {
        r[col] = lastVals[col] ?? "";
      }
    }
    return r;
  });

  const results = [];
  for (const row of filled) {
    for (const [key, value] of Object.entries(row)) {
      if (idCols.includes(key) || key === "year Change" || value == null) continue;
      let numVal = typeof value === "number" ? value : parseFloat(value);
      if (isNaN(numVal)) continue;

      const commodity = row.commodity || "";
      const attribute = row.attribute || "";
      const cnCode = row.cn_Code || "";
      const country = row.country || "";
      let unit = row["unit Description"] || "";

      // Duas taxas que o PSD manda numa escala diferente da unidade que
      // declara: a de extração vem como fração (0,783 = 78,3%) com unidade
      // PERCENT, e o rendimento do arroz em décimos de milésimo (6691 =
      // 66,91%) com unidade "1000 MT". Sem isso a tela mostrava "0,78%" e
      // "6,69 mil toneladas".
      if (/^extr\. rate/i.test(attribute)) numVal *= 100;
      if (/^milling rate/i.test(attribute)) {
        numVal /= 100;
        unit = "(PERCENT)";
      }

      const mObj = mercadoMap.get(commodity.toLowerCase().trim());

      // Pecuária e laticínios não têm safra: no PSD o "ano comercial" deles
      // é o ano civil (marketYear 2026 = jan-dez/2026, que a API devolve na
      // coluna "2026/2027"). Neles o rótulo é só o ano; nos grãos, a safra.
      const porAno = MERCADOS_POR_ANO.has(mObj?.TipoMercado);
      const safra =
        key.length >= 6 && !porAno
          ? key.substring(0, 4) + "/" + key.slice(-2)
          : key.substring(0, 4);
      const anoNum = parseInt(key.substring(0, 4), 10) || 0;
      const pRef =
        paisBySigla.get(cnCode) ||
        paisByCountry.get(country.toLowerCase().trim());

      results.push({
        commodity,
        attribute,
        cnCode,
        country,
        unit,
        safra,
        anoNum,
        valor: numVal,
        valorFinal: numVal,
        nomeMercado: mObj?.NomeMercado || commodity,
        tipoMercado: mObj?.TipoMercado || "",
        atributo: atributoMap.get(attribute.toLowerCase().trim()) || attribute,
        pais: pRef?.Pais || country,
        regiao: pRef?.Regiao || "",
      });
    }
  }
  return results;
}

// ---------- Componente principal ----------
export default function USDA() {
  const navigate = useNavigate();
  const ehPopup = useMemo(() => new URLSearchParams(window.location.search).get("popup") === "1", []);
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [cacheInfo, setCacheInfo] = useState(null); // { fromCache, cachedAt } | null
  const [embedModalVisible, setEmbedModalVisible] = useState(false);
  const [embedHtml, setEmbedHtml] = useState('');
  const [isMundo, setIsMundo] = useState(false);
  const [isRegiao, setIsRegiao] = useState(false);
  const chartDimRef = useRef({ width: 500, height: 300 });
  const [chartColor, setChartColor] = useState("#39FF14");
  const [formCollapsed, setFormCollapsed] = useState(false);
  const [copiedKey, setCopiedKey] = useState(null);

  // Filtros pré-busca
  const [regiaoSelecionada, setRegiaoSelecionada] = useState(null);
  const [paisesSelecionados, setPaisesSelecionados] = useState([]);
  const [anoInicio, setAnoInicio] = useState(new Date().getFullYear() - 5);
  const [anoFim, setAnoFim] = useState(new Date().getFullYear());

  // Filtros pós-busca
  const [tipoSelecionado, setTipoSelecionado] = useState(null);
  const [commoditySelecionada, setCommoditySelecionada] = useState([]);
  const [atributosSelecionados, setAtributosSelecionados] = useState([]);

  // Países filtrados pela região
  const paisesFiltrados = useMemo(() => {
    const list = regiaoSelecionada
      ? paisRegiao.filter((p) => p.Regiao === regiaoSelecionada)
      : paisRegiao;
    const seen = new Set();
    return list.filter((p) => {
      if (seen.has(p.Sigla)) return false;
      seen.add(p.Sigla);
      return true;
    });
  }, [regiaoSelecionada]);

  // Commodities disponíveis nos dados retornados
  const commoditiesDisponiveis = useMemo(() => {
    const set = new Set();
    return data.reduce((acc, d) => {
      if (!set.has(d.commodity)) {
        set.add(d.commodity);
        acc.push({
          commodity: d.commodity,
          nomeMercado: d.nomeMercado,
          tipoMercado: d.tipoMercado,
        });
      }
      return acc;
    }, []);
  }, [data]);

  const commoditiesFiltradas = tipoSelecionado
    ? commoditiesDisponiveis.filter((c) => c.tipoMercado === tipoSelecionado)
    : commoditiesDisponiveis;

  // Atributos disponíveis (filtrados pelas commodities selecionadas)
  const atributosDisponiveis = useMemo(() => {
    const filtered = commoditySelecionada.length
      ? data.filter((d) => commoditySelecionada.includes(d.commodity))
      : data;
    const set = new Set();
    return filtered.reduce((acc, d) => {
      if (!set.has(d.attribute)) {
        set.add(d.attribute);
        acc.push({ attribute: d.attribute, atributo: d.atributo });
      }
      return acc;
    }, []);
  }, [data, commoditySelecionada]);

  const handleCommodityChange = useCallback((v) => {
    setCommoditySelecionada(v ?? []);
    setAtributosSelecionados([]);
  }, []);

  // ---------- Gerar grade de mini-gráficos ----------
  const miniCharts = useMemo(() => {
    if (!commoditySelecionada.length || !atributosSelecionados.length) return [];

    const filtered = data.filter(
      (d) =>
        commoditySelecionada.includes(d.commodity) &&
        atributosSelecionados.includes(d.attribute)
    );
    if (!filtered.length) return [];

    // Agrupar por attribute + local (país, região ou "Mundo")
    const groups = new Map();
    // Em "Mundo" vale só a linha World do USDA; numa região, todos os
    // países menos ela (a região "Mundo" da lista é o próprio R00).
    const fonte = isMundo
      ? filtered.filter((d) => d.cnCode === CODIGO_MUNDO)
      : isRegiao && filtered.some((d) => d.cnCode !== CODIGO_MUNDO)
      ? filtered.filter((d) => d.cnCode !== CODIGO_MUNDO)
      : filtered;
    for (const d of fonte) {
      const local = isMundo
        ? "Mundo"
        : isRegiao
        ? (d.regiao || regiaoSelecionada || "Região")
        : (d.pais || d.country);
      const key = `${d.commodity}|||${d.attribute}|||${local}`;
      if (!groups.has(key)) {
        groups.set(key, {
          commodity: d.commodity,
          attribute: d.attribute,
          atributo: d.atributo,
          local,
          nomeMercado: d.nomeMercado,
          unit: d.unit,
          points: [],
        });
      }
      if (isRegiao) {
        // Somar valores por safra (o que não se soma é refeito abaixo)
        const existing = groups.get(key).points;
        const ep = existing.find((p) => p.safra === d.safra);
        if (ep) {
          ep.valorFinal += d.valorFinal;
        } else {
          existing.push({ ...d, pais: local, country: local });
        }
      } else {
        groups.get(key).points.push(d);
      }
    }

    // Região: produtividade refeita como produção / área; percentual e
    // razão saem do agregado (não há como somá-los).
    // (A região "Mundo" é só o R00: nada a refazer, o valor já é o oficial.)
    if (isRegiao && fonte.some((d) => d.cnCode !== CODIGO_MUNDO)) {
      const doLocal = data.filter((d) => d.cnCode !== CODIGO_MUNDO);
      for (const [key, g] of groups) {
        if (!unidadeNaoSomavel(g.unit)) continue;
        const refeitos = produtividadeAgregada(g.points, doLocal);
        if (refeitos && refeitos.length) g.points = refeitos;
        else groups.delete(key);
      }
    }

    // Ordenar: por commodity, depois por atributo, depois por local
    const commOrder = new Map(commoditySelecionada.map((c, i) => [c, i]));
    const attrOrder = new Map(atributosSelecionados.map((a, i) => [a, i]));
    const sorted = [...groups.values()].sort((a, b) => {
      const ic = (commOrder.get(a.commodity) ?? 999) - (commOrder.get(b.commodity) ?? 999);
      if (ic !== 0) return ic;
      const ia = (attrOrder.get(a.attribute) ?? 999) - (attrOrder.get(b.attribute) ?? 999);
      if (ia !== 0) return ia;
      return a.local.localeCompare(b.local);
    });

    return sorted.map((g) => {
      // Medida do mercado (milhão/cab, milhão/t, t/ha, milhão de sacas…):
      // a mesma na tela, no código incorporado e no gerador de imagens.
      const maior = Math.max(...g.points.map((p) => Math.abs(p.valorFinal || 0)), 0);
      const medida = medidaUsda(g.unit, maior);
      const formatarValor = (v) => formatarNaMedida(v, medida);
      const pontos = [...g.points].sort((a, b) => a.anoNum - b.anoNum);
      return {
        // Com a commodity: milho e soja com o mesmo atributo e país geravam
        // a mesma key, e o React podia trocar um gráfico pelo outro.
        key: `${g.commodity}-${g.attribute}-${g.local}`,
        options: buildMiniChartOptions(g.points, g.nomeMercado, g.atributo, g.local, medida.unidade, chartColor, {
          formatarValor,
        }),
        medida,
        embedData: {
          nomeMercado: g.nomeMercado,
          atributo: g.atributo,
          local: g.local,
          unit: g.unit,
          unidadeTexto: medida.unidade,
          color: chartColor,
          sortedPoints: pontos.map((p) => ({
            safra: p.safra,
            anoNum: p.anoNum,
            valor: p.valorFinal,
            rotulo: formatarValor(p.valorFinal),
          })),
        },
      };
    });
  }, [data, commoditySelecionada, atributosSelecionados, isMundo, isRegiao, regiaoSelecionada, chartColor]);

  // ---------- Levar um gráfico ao Gerador de imagens ----------
  const abrirNoGerador = (mc) => {
    try {
      sessionStorage.setItem(CHAVE_SPEC_IMPORTADO, JSON.stringify(specUsdaParaGerador(mc.embedData, mc.medida)));
    } catch {}
    navigate(`/graficos/gerador${ehPopup ? "?popup=1" : ""}`);
  };

  // ---------- Copiar embed individual ----------
  const handleCopySingleEmbed = (mc) => {
    const { width, height } = chartDimRef.current;
    const html = generateEmbedHtml([mc], width, height);
    navigator.clipboard.writeText(html)
      .then(() => {
        setCopiedKey(mc.key);
        setTimeout(() => setCopiedKey(null), 2000);
      })
      .catch(() => message.error("Erro ao copiar."));
  };

  // ---------- Busca na API USDA ----------
  const handleSearch = async () => {
    if (!paisesSelecionados.length) {
      message.warning("Selecione ao menos um país/região");
      return;
    }
    setLoading(true);
    setData([]);
    setCacheInfo(null);
    setCommoditySelecionada([]);
    setAtributosSelecionados([]);
    setTipoSelecionado(null);

    const years = [];
    for (let y = anoFim; y >= anoInicio; y--) years.push(y);

    try {
      // "Mundo" pede só a linha World do USDA: é o total oficial (com
      // produtividade e estoques certos) e uma consulta só, em vez de 251
      // países somados.
      const json = await request("/usda/query", {
        method: "POST",
        body: JSON.stringify({ countries: isMundo ? [CODIGO_MUNDO] : paisesSelecionados, marketYears: years }),
      });
      const processed = processApiResponse(json.queryResult || []);
      setData(processed);
      if (json.fromCache) {
        setCacheInfo({ fromCache: true, cachedAt: json.cachedAt });
        message.warning("Exibindo dados em cache — a API do USDA está indisponível no momento.");
      } else {
        setCacheInfo(null);
      }
      if (processed.length) setFormCollapsed(true);
      else message.info("Nenhum dado encontrado");
    } catch (err) {
      message.error("Erro ao consultar USDA: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  // ---------- Render ----------
  return (
    <Content style={{ margin: "12px 16px" }}>
      <Title level={3}>USDA — Projeção USDA</Title>

      {cacheInfo?.fromCache && (
        <Alert
          type="warning"
          showIcon
          message={`Exibindo dados em cache de ${new Date(cacheInfo.cachedAt).toLocaleString("pt-BR")} — a API do USDA está indisponível no momento.`}
          style={{ marginBottom: 16 }}
        />
      )}

      <Row gutter={[16, 16]}>
        {/* ===== FILTROS ===== */}
        <Col xs={24} lg={6} xl={5}>
          <Card title="Filtros" size="small">
            <Form layout="vertical" form={form} size="small">

              {/* ---- Resumo colapsado ---- */}
              {formCollapsed ? (
                <>
                  <div style={{ fontSize: 12, color: "#999", marginBottom: 4 }}>Países</div>
                  <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8 }}>
                    {isMundo
                      ? "🌍 Mundo (todos)"
                      : isRegiao
                      ? `🗺️ ${regiaoSelecionada}`
                      : paisesSelecionados.map(s => paisBySigla.get(s)?.Pais || s).join(", ")}
                  </div>
                  <div style={{ fontSize: 12, color: "#999", marginBottom: 4 }}>Período</div>
                  <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 12 }}>
                    {anoInicio} — {anoFim}
                  </div>
                  <Button
                    icon={<EditOutlined />}
                    block
                    size="small"
                    onClick={() => setFormCollapsed(false)}
                  >
                    Editar país/região/período
                  </Button>
                  <Divider style={{ margin: "12px 0" }} />
                </>
              ) : (
                <>
                  <Divider orientation="left" plain style={{ margin: "4px 0 12px" }}>
                    Localização
                  </Divider>

                  <Form.Item label="Região">
                    <Select
                      value={regiaoSelecionada}
                      onChange={(v) => {
                        setRegiaoSelecionada(v);
                        setIsMundo(false);
                        if (v) {
                          setIsRegiao(true);
                          const seen = new Set();
                          const siglas = paisRegiao
                            .filter((p) => p.Regiao === v)
                            .reduce((acc, p) => {
                              if (!seen.has(p.Sigla)) { seen.add(p.Sigla); acc.push(p.Sigla); }
                              return acc;
                            }, []);
                          setPaisesSelecionados(siglas);
                        } else {
                          setIsRegiao(false);
                          setPaisesSelecionados([]);
                        }
                      }}
                      allowClear
                      showSearch
                      placeholder="Todas as regiões"
                      options={regioes.map((r) => ({ value: r, label: r }))}
                    />
                  </Form.Item>

                  <Form.Item label="País / Região">
                    <Select
                      mode="multiple"
                      value={paisesSelecionados}
                      onChange={(values) => {
                        if (values.includes("__MUNDO__")) {
                          setIsMundo(true);
                          setIsRegiao(false);
                          setPaisesSelecionados(paisesFiltrados.map((p) => p.Sigla));
                        } else {
                          setIsMundo(false);
                          setIsRegiao(false);
                          setPaisesSelecionados(values);
                        }
                      }}
                      allowClear
                      showSearch
                      placeholder="Selecione o(s) país(es)"
                      optionFilterProp="label"
                      maxTagCount={3}
                      options={[
                        { value: "__MUNDO__", label: "🌍 Mundo (todos)" },
                        ...paisesFiltrados.map((p) => ({
                          value: p.Sigla,
                          label: p.Pais,
                        })),
                      ]}
                    />
                  </Form.Item>

                  <Divider orientation="left" plain style={{ margin: "4px 0 12px" }}>
                    Período
                  </Divider>

                  <Row gutter={8}>
                    <Col span={12}>
                      <Form.Item label="Ano Início">
                        <Select
                          value={anoInicio}
                          onChange={setAnoInicio}
                          showSearch
                          options={YEARS.map((y) => ({ value: y, label: String(y) }))}
                        />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item label="Ano Fim">
                        <Select
                          value={anoFim}
                          onChange={setAnoFim}
                          showSearch
                          options={YEARS.map((y) => ({ value: y, label: String(y) }))}
                        />
                      </Form.Item>
                    </Col>
                  </Row>

                  <Button
                    type="primary"
                    icon={<SearchOutlined />}
                    onClick={handleSearch}
                    loading={loading}
                    block
                    size="middle"
                    style={{ marginTop: 8 }}
                  >
                    Buscar Dados USDA
                  </Button>
                </>
              )}

              {data.length > 0 && (
                <>
                  <Divider
                    orientation="left"
                    plain
                    style={{ margin: "16px 0 12px" }}
                  >
                    Gráficos
                  </Divider>

                  <Form.Item label="Tipo de Mercado">
                    <Select
                      value={tipoSelecionado}
                      onChange={(v) => {
                        setTipoSelecionado(v);
                        setCommoditySelecionada([]);
                        setAtributosSelecionados([]);
                      }}
                      allowClear
                      showSearch
                      placeholder="Todos os tipos"
                      options={tiposMercado.map((t) => ({ value: t, label: t }))}
                    />
                  </Form.Item>

                  <Form.Item label="Commodity">
                    <Select
                      mode="multiple"
                      value={commoditySelecionada}
                      onChange={handleCommodityChange}
                      allowClear
                      showSearch
                      placeholder="Selecione os mercados"
                      optionFilterProp="label"
                      maxTagCount={2}
                      options={commoditiesFiltradas.map((c) => ({
                        value: c.commodity,
                        label: `${c.nomeMercado} (${c.commodity})`,
                      }))}
                    />
                  </Form.Item>

                  <Form.Item label="Atributos">
                    <Select
                      mode="multiple"
                      value={atributosSelecionados}
                      onChange={setAtributosSelecionados}
                      allowClear
                      showSearch
                      placeholder="Selecione os atributos"
                      optionFilterProp="label"
                      maxTagCount={2}
                      options={atributosDisponiveis.map((a) => ({
                        value: a.attribute,
                        label: `${a.atributo} (${a.attribute})`,
                      }))}
                    />
                  </Form.Item>

                  <Form.Item label="Cor do Gráfico">
                    <Space>
                      <input
                        type="color"
                        value={chartColor}
                        onChange={(e) => setChartColor(e.target.value)}
                        style={{ width: 40, height: 28, padding: 2, cursor: "pointer", borderRadius: 4, border: "1px solid #d9d9d9" }}
                      />
                      <span style={{ fontSize: 12, fontFamily: "monospace" }}>{chartColor.toUpperCase()}</span>
                      <Button size="small" onClick={() => setChartColor("#39FF14")}>Reset</Button>
                    </Space>
                  </Form.Item>
                </>
              )}
            </Form>
          </Card>
        </Col>

        {/* ===== GRADE DE MINI-GRÁFICOS ===== */}
        <Col xs={24} lg={18} xl={19}>
          {loading ? (
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                minHeight: 400,
              }}
            >
              <Spin size="large" />
            </div>
          ) : miniCharts.length > 0 ? (
            <>
              <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 12 }}>
                <Button
                  icon={<CodeOutlined />}
                  onClick={() => {
                    const { width, height } = chartDimRef.current;
                    const html = generateEmbedHtml(miniCharts, width, height);
                    setEmbedHtml(html);
                    setEmbedModalVisible(true);
                  }}
                >
                  Gerar código para incorporar
                </Button>
              </div>
              <Row gutter={[12, 12]}>
                {miniCharts.map(
                  (mc, idx) =>
                    mc.options && (
                      <Col key={mc.key} xs={24} sm={24} lg={12} xl={12}>
                        <div>
                          {/* Botões numa faixa acima do gráfico: por cima dele
                              eles cobriam o título. */}
                          <div style={{ display: "flex", justifyContent: "flex-end", gap: 6, marginBottom: 4 }}>
                            <Button size="small" icon={<EditOutlined />} onClick={() => abrirNoGerador(mc)} style={{ fontSize: 11 }}>
                              Gerador
                            </Button>
                            <Button
                              size="small"
                              icon={<CopyOutlined />}
                              onClick={() => handleCopySingleEmbed(mc)}
                              style={{
                                fontSize: 11,
                                background: copiedKey === mc.key ? "#27ae60" : undefined,
                                borderColor: copiedKey === mc.key ? "#27ae60" : undefined,
                                color: copiedKey === mc.key ? "#fff" : undefined,
                              }}
                            >
                              {copiedKey === mc.key ? "Copiado!" : "Copiar"}
                            </Button>
                          </div>
                          <HighchartsReact
                            highcharts={Highcharts}
                            options={mc.options}
                            callback={idx === 0 ? (chart) => {
                              // O código incorporado segue com 500px, como
                              // sempre: a largura da tela agora varia com a coluna.
                              chartDimRef.current = { width: 500, height: chart.chartHeight };
                            } : undefined}
                          />
                        </div>
                      </Col>
                    )
                )}
              </Row>
            </>
          ) : (
            <Card size="small">
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  alignItems: "center",
                  minHeight: 400,
                  color: "#999",
                }}
              >
                <Title level={4} type="secondary">
                  {data.length > 0
                    ? "Selecione a Commodity para gerar os gráficos"
                    : 'Selecione o país e clique em "Buscar Dados USDA"'}
                </Title>
              </div>
            </Card>
          )}
        </Col>
      </Row>

      <Modal
        title="Código HTML para incorporar"
        open={embedModalVisible}
        onCancel={() => setEmbedModalVisible(false)}
        width={800}
        footer={
          <Button
            type="primary"
            icon={<CopyOutlined />}
            onClick={() => {
              navigator.clipboard.writeText(embedHtml)
                .then(() => message.success("Código copiado para a área de transferência!"))
                .catch(() => message.error("Erro ao copiar. Selecione manualmente."));
            }}
          >
            Copiar código
          </Button>
        }
      >
        <Input.TextArea
          value={embedHtml}
          autoSize={{ minRows: 8, maxRows: 20 }}
          readOnly
          style={{ fontFamily: "monospace", fontSize: 11 }}
        />
      </Modal>
    </Content>
  );
}
